import { useCallback, useMemo } from 'react';
import { VictoryPie } from 'victory';
import { darkenColor, getProfitColor } from '../../../../../helpers/colors';
import {
  formatBalance,
  formatPercentage,
} from '../../../../../helpers/NumberFormatter';
import { getBotColor } from '../helpers';
import { SortingToggleOptions } from '../../../types';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setActiveSubscriptionId } from '../../../../../store/slices/subscription';
import { useSelector } from 'react-redux';
import {
  selectOverviewChartPercentage,
  selectOverviewValuesHidden,
} from '../../../../../store/slices/ui';

interface Props {
  data: Array<{ x: string; y: number; value?: number; subscriptionId: number }>;
  activeToggleOption: SortingToggleOptions;
  showAllData: boolean | undefined;
  width: number;
  height: number;
  hideLabels?: boolean;
}

const PieChart = ({
  data,
  activeToggleOption,
  showAllData,
  width,
  height,
  hideLabels,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isRelative = useSelector(selectOverviewChartPercentage);
  const valuesHidden = useSelector(selectOverviewValuesHidden);

  const diversityColorScale = useMemo(
    () => data.map((datum) => getBotColor(datum.x)),
    [data],
  );

  const profitColorScale = useMemo(
    () => data.map((datum) => getProfitColor(datum.value ?? datum.y)),
    [data],
  );

  const colorScale = useMemo(
    () =>
      activeToggleOption === SortingToggleOptions.Diversity ||
      activeToggleOption === SortingToggleOptions.TotalValue
        ? diversityColorScale
        : profitColorScale,
    [activeToggleOption, diversityColorScale, profitColorScale],
  );

  const totalValue = useMemo(() => {
    return data.reduce((acc, curr) => acc + Math.abs(curr.y), 0);
  }, [data]);

  const createLabel = useCallback(
    (datum: any) => {
      // Percentages stay visible; only absolute currency values are hidden.
      const showAsRelative = isRelative || activeToggleOption.includes('Asset');
      if (showAsRelative) {
        return `${datum.x}\n${formatPercentage(
          datum.labelValue ?? datum.value ?? datum.y,
          1,
        )}`;
      }
      if (valuesHidden) {
        return `${datum.x}\n••••`;
      }
      return `${datum.x}\n${formatBalance(datum.value ?? datum.y, true)}`;
    },
    [activeToggleOption, isRelative, valuesHidden],
  );

  const labels = useMemo(
    () =>
      data.map((datum) => {
        const label = createLabel(datum);
        return datum.y / totalValue > 0.023 ? label : '';
      }),
    [createLabel, data, totalValue],
  );

  const maxValue = useMemo(
    () => Math.max(...data.map((d) => d.y || 0)),
    [data],
  );
  const minValue = useMemo(
    () => Math.min(...data.map((d) => d.y || 0)),
    [data],
  );

  const getRadius = useCallback(
    ({ datum }: any) => {
      // 10% smaller pie when used inside the overview chart cards
      const base = (height / 2.5) * (showAllData ? 1.2 : 1) * 0.9;

      const diffOriginal = maxValue - minValue;
      const a = datum.y;
      const aPerc = (a - minValue) / diffOriginal;
      const ratio = 0.65 * (showAllData ? 0.9 : 1);
      return base * Math.max(0.5, Math.min(1, ratio + ratio * aPerc));
    },
    [height, showAllData, maxValue, minValue],
  );

  const getLabelRadius = useCallback(
    ({ datum }: any) => getRadius({ datum }) + 10,
    [getRadius],
  );

  const style = useMemo(
    () => ({
      labels: {
        fontSize: 8,
        fill: 'white',
        fontWeight: 700,
        opacity: hideLabels ? 0 : showAllData ? 1 : 0,
      },
    }),
    [showAllData, hideLabels],
  );

  return (
    <VictoryPie
      key={`${isRelative}`}
      width={width * (showAllData ? 1 : 1)}
      height={height * (showAllData ? 1 : 1)}
      data={data}
      colorScale={colorScale}
      padAngle={2}
      innerRadius={Math.max(8, Math.min(28, height * 0.07))}
      style={style}
      cornerRadius={2}
      labels={labels}
      labelRadius={getLabelRadius}
      radius={getRadius}
      events={[
        {
          target: 'data',
          eventHandlers: {
            onClick: () => {
              return [
                {
                  target: 'data',
                  mutation: async ({ datum }) => {
                    if (!showAllData) {
                      return;
                    }

                    void dispatch(
                      setActiveSubscriptionId(datum.subscriptionId),
                    );
                    navigate('/sub');
                    return null; // No visual mutation on click
                  },
                },
              ];
            },
            onMouseOver: () => {
              return [
                {
                  target: 'data',
                  mutation: ({ datum, index }) => {
                    return {
                      radius: getRadius({ datum }) + 5, // Increase radius on hover
                      style: { fill: darkenColor(colorScale[index]) },
                    };
                  },
                },
                {
                  target: 'labels',
                  mutation: ({ datum }) => ({
                    text: createLabel(datum),
                    style: { ...style.labels, fontSize: 9, opacity: 1 },
                  }),
                },
              ];
            },
            onMouseOut: () => {
              return [
                {
                  target: 'data',
                  mutation: ({ datum }) => ({ radius: getRadius({ datum }) }), // Reset radius
                },
                {
                  target: 'labels',
                  mutation: () => ({
                    style: style.labels,
                  }),
                },
              ];
            },
          },
        },
      ]}
    />
  );
};

export default PieChart;
